import React, { useState, useEffect } from 'react';
import InputField from '../components/ui/InputField';
import { MdEmail, MdLock, MdPerson, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { BiLoaderAlt } from 'react-icons/bi'; // Add this import
import { useAppDispatch } from '../hooks/useAppDispatch';
import { useAppSelector } from '../hooks/useAppSelector';
import { useNavigate, Link } from 'react-router-dom';
import { loginUser } from '../redux/actions/authActions';
import { clearAuthState, resetForgotPasswordState } from '../redux/slices/authSlice';
import Logo from '../assets//manak-logo.png';

import Carousel_1 from '../assets/carousel_1.png';
import Carousel_2 from '../assets/carousel_2.png';
import Carousel_3 from '../assets/carousel_3.png';
import Carousel_4 from '../assets/carousel_4.png';

const Login = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [loginFormData, setLoginFormData] = useState({
    email: '',
    password: '',
    role: 'Employee', // Maintain 'Employee' as the actual value
    rememberMe: false,
  });
  const [loginError, setLoginError] = useState('');
  const [showLoginForm, setShowLoginForm] = useState(false);
  const isMobile = window.innerWidth < 768;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading: isLoading, error, isAuthenticated } = useAppSelector(state => state.auth);

  // Define role options with display name and actual value
  const roleOptions = [
    { display: 'Admin', value: 'admin' },
    { display: 'User', value: 'employee' }, // Display "User" but use "Employee" value
  ];

  // Define carousel content
  const carouselItems = [
    {
      id: 0,
      title: 'Financial Tracking and Billing',
      image: Carousel_1,
      description: 'Track your finances and manage billing efficiently',
    },
    {
      id: 1,
      title: 'Smart Task Management',
      image: Carousel_2,
      description: 'Organize and prioritize tasks effectively',
    },
    {
      id: 2,
      title: 'Track Projects with Ease',
      image: Carousel_3,
      description: 'Monitor project progress seamlessly',
    },
    {
      id: 3,
      title: 'SeamLess Team Collaboration',
      image: Carousel_4,
      description: 'Work together efficiently with your team',
    },
  ];

  // Auto-run slider
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide(prev => (prev + 1) % carouselItems.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Load remembered credentials if available
  useEffect(() => {
    try {
      const rememberedCredentials = localStorage.getItem('rememberedCredentials');
      if (rememberedCredentials) {
        const { email, role } = JSON.parse(rememberedCredentials);
        setLoginFormData(prev => ({
          ...prev,
          email,
          role,
          rememberMe: true,
        }));
      }
    } catch (error) {
      console.error('Failed to load remembered credentials:', error);
    }
  }, []);

  // Check if any of the auth tokens exist in cookies
  const checkForTokens = () => {
    if (!document.cookie) return false;

    const cookies = document.cookie.split('; ');
    const tokenNames = ['pms_admin_', 'pms_employee_'];
    
    for (const tokenName of tokenNames) {
      for (const cookie of cookies) {
        if (cookie.startsWith(`${tokenName}=`)) {
          return true;
        }
      }
    }
    return false;
  };

  // Redirect if already authenticated
  useEffect(() => {
    // Keep track of whether we've redirected to prevent multiple redirects
    let hasRedirected = false;
    let redirectTimer: ReturnType<typeof setTimeout>;
    
    const hasValidTokens = checkForTokens();
    
    // Only redirect if BOTH Redux state says authenticated AND we have actual tokens
    if (isAuthenticated && hasValidTokens && !hasRedirected) {
      hasRedirected = true;
      console.log('User is authenticated with valid tokens, preparing to redirect to dashboard');
      
      // Add a small delay to prevent race conditions with other auth effects
      redirectTimer = setTimeout(() => {
        console.log('Redirecting authenticated user to dashboard');
        navigate('/dashboard', { replace: true });
      }, 300);
    } else if (isAuthenticated && !hasValidTokens) {
      // If Redux thinks we're authenticated but no tokens exist, clear the auth state
      console.log('Redux state shows authenticated but no tokens found, clearing auth state');
      dispatch(clearAuthState());
    }
    
    // Cleanup function to prevent memory leaks and cancel pending redirects
    return () => {
      if (redirectTimer) {
        clearTimeout(redirectTimer);
      }
    };
  }, [isAuthenticated, navigate, dispatch]);

  // Handle form input changes
  const handleInputChange = (field: string, value: string | boolean) => {
    setLoginFormData({
      ...loginFormData,
      [field]: value,
    });
  };

  // Handle login form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');

    // Validate form
    if (!loginFormData.email || !loginFormData.password) {
      setLoginError('Please enter both email and password');
      return;
    }

    try {
      await dispatch(
        loginUser({
          email: loginFormData.email,
          password: loginFormData.password,
          role: loginFormData.role,
          rememberMe: loginFormData.rememberMe,
        })
      ).unwrap();

      // Navigation will be handled by the useEffect watching isAuthenticated
    } catch (err: any | unknown) {
      // Error display is handled by the component through Redux state
      console.error('Login failed:', err.message);
    }
  };

  // const slides = [
  //   { id: 0, color: 'bg-purple-600' },
  //   { id: 1, color: 'bg-gray-300' },
  //   { id: 2, color: 'bg-gray-300' },
  // ];

  const mobileCarouselData = [
    {
      title: 'Financial Tracking and Billing',
      description: 'Track your finances and manage billing efficiently',
      image: Carousel_1,
    },
    {
      title: 'Smart Task Management',
      description: 'Organize and prioritize tasks effectively',
      image: Carousel_2,
    },
    {
      title: 'Track Projects with Ease',
      description: 'Monitor project progress seamlessly',
      image: Carousel_3,
    },
  ];

  const MobileCarousel = () => (
    <div className="flex flex-col min-h-[500px] justify-between">
      <div className="flex-1">
        {/* Add Logo */}
        <div className="flex justify-center mb-6">
          <img src={Logo} alt="Manak Logo" className="h-12" />
        </div>

        <h2 className="text-2xl font-bold text-red-600 mb-6 text-center">
          Project Management System
        </h2>

        {/* Carousel Container */}
        <div className="relative h-[300px] mb-6">
          {mobileCarouselData.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-all duration-500 flex flex-col ${
                activeSlide === index ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
            >
              <div className="w-full h-48 flex items-center justify-center mb-4">
                <img
                  src={slide.image}
                  alt={slide.title}
                  className="max-w-full max-h-full object-contain rounded-lg"
                />
              </div>
              <div className="text-center px-4">
                <h3 className="text-xl font-bold mb-2">{slide.title}</h3>
                <p className="text-gray-600 text-sm">{slide.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Dots Navigation */}
        <div className="flex justify-center gap-2 mb-8">
          {mobileCarouselData.map((_, index) => (
            <button
              key={index}
              className={`w-2 h-2 rounded-full ${
                activeSlide === index ? 'bg-red-600' : 'bg-gray-300'
              }`}
              onClick={() => setActiveSlide(index)}
            />
          ))}
        </div>
      </div>

      <button
        onClick={() => setShowLoginForm(true)}
        className="w-full bg-red-600 text-white py-3 rounded-lg text-base font-semibold mt-auto"
      >
        Get Started
      </button>
    </div>
  );

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-white relative overflow-hidden">
      {/* Background Pattern SVG - Keep existing pattern */}
      <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern
            id="wave-pattern"
            patternUnits="userSpaceOnUse"
            width="100"
            height="8"
            patternTransform="rotate(0)"
          >
            <path
              d="M0,4 C12.5,0 12.5,8 25,4 C37.5,0 37.5,8 50,4 C62.5,0 62.5,8 75,4 C87.5,0 87.5,8 100,4"
              fill="none"
              stroke="rgba(232, 180, 180, 1)"
              strokeWidth="0.6"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#wave-pattern)" />
      </svg>

      {/* Main Content */}
      <div className="flex flex-col md:flex-row w-full max-w-6xl px-4 md:px-8 z-10">
        {/* Left Side - Show only on desktop */}
        {!isMobile && (
          <div className="w-full md:w-1/2 pr-0 md:pr-8 flex flex-col items-center justify-center mb-8 md:mb-0">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center w-full">
              <span className="text-red-600">
                {carouselItems[activeSlide].title.split(' ').slice(0, 2).join(' ')}
              </span>
              <br />
              <span className="text-red-600">
                {carouselItems[activeSlide].title.split(' ').slice(2).join(' ')}
              </span>
            </h1>

            <div className="mt-4 md:mt-8 w-full max-w-md relative overflow-hidden">
              {/* Slider container - increased height further */}
              <div className="relative w-full h-96">
                {carouselItems.map(item => (
                  <div
                    key={item.id}
                    className={`absolute inset-0 transition-opacity duration-1000 ${activeSlide === item.id ? 'opacity-100' : 'opacity-0'}`}
                  >
                    <div className="relative h-full flex items-center justify-center">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-contain max-h-96"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Dots Navigation */}
            <div className="flex items-center justify-center mt-6 self-center">
              {carouselItems.map(item => (
                <div
                  key={item.id}
                  className={`h-1 rounded-full mx-1 transition-all duration-300 cursor-pointer ${
                    activeSlide === item.id ? 'w-8 bg-red-600' : 'w-4 bg-gray-300'
                  }`}
                  onClick={() => setActiveSlide(item.id)}
                ></div>
              ))}
            </div>
          </div>
        )}

        {/* Right Side / Mobile Full Width */}
        <div className={`w-full ${!isMobile ? 'md:w-1/2' : ''} flex justify-center`}>
          <div className="bg-white p-6 md:p-10 rounded-2xl shadow-lg w-full max-w-xl">
            {isMobile && !showLoginForm ? (
              <MobileCarousel />
            ) : (
              // Login Form View
              <>
                {isMobile && (
                  <button
                    onClick={() => setShowLoginForm(false)}
                    className="mb-4 text-gray-600 flex items-center text-sm"
                  >
                    ← Back
                  </button>
                )}
                <div className={`${isMobile ? 'animate-slide-left' : ''}`}>
                  <h2 className="text-2xl md:text-4xl font-bold text-red-600 mb-2 md:mb-4">
                    Hi, Welcome to MESPL
                  </h2>
                  <h3 className="text-xl md:text-3xl font-semibold text-red-600 mb-3 md:mb-6">
                    Sign in to your session
                  </h3>

                  <p className="text-gray-500 mb-6 md:mb-8">Manage Your Projects Efficiently!</p>

                  {/* Display error message if any */}
                  {(error || loginError) && (
                    <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
                      {error || loginError}
                    </div>
                  )}

                  <form onSubmit={handleSubmit}>
                    <div className="mb-6">
                      <InputField
                        label="Email"
                        value={loginFormData.email}
                        onChange={e => handleInputChange('email', e.target.value)}
                        placeholder="Enter your email"
                        icon={<MdEmail className="w-5 h-5" />}
                        labelClassName="text-red-600"
                      />
                    </div>

                    <div className="mb-6 relative">
                      <InputField
                        label="Password"
                        type="password"
                        value={loginFormData.password}
                        onChange={e => handleInputChange('password', e.target.value)}
                        placeholder="Enter your password"
                        icon={<MdLock className="w-5 h-5" />}
                        labelClassName="text-red-600"
                      />
                    </div>

                    {/* Role Selection */}
                    <div className="mb-8">
                      <label className="flex items-center text-red-600 mb-3">
                        <MdPerson className="w-5 h-5 mr-2" />
                        Login as
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {roleOptions.map(role => (
                          <div
                            key={role.value}
                            onClick={() => handleInputChange('role', role.value)}
                            className={`
                              px-4 py-3 rounded-lg border-2 cursor-pointer transition-all duration-200 text-center
                              ${
                                loginFormData.role === role.value
                                  ? 'border-red-600 bg-red-50 text-red-800 font-medium shadow-sm'
                                  : 'border-gray-300 hover:border-gray-400'
                              }
                            `}
                          >
                            {role.display}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex justify-between items-center mb-8">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="remember"
                          className="h-5 w-5 text-red-500 focus:ring-red-500"
                          checked={loginFormData.rememberMe}
                          onChange={e => handleInputChange('rememberMe', e.target.checked)}
                        />
                        <label htmlFor="remember" className="ml-2 text-gray-700 text-lg">
                          Remember me
                        </label>
                      </div>
                      <Link 
                        to="/auth/forgot-password" 
                        onClick={() => dispatch(resetForgotPasswordState())}
                        className="text-blue-600 hover:text-blue-700 font-medium text-lg"
                      >
                        Forgot Password?
                      </Link>
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading}
                      className={`w-full bg-red-600 text-white py-4 rounded-lg flex items-center justify-center font-medium text-lg ${
                        isLoading ? 'opacity-70 cursor-not-allowed' : ''
                      }`}
                    >
                      {isLoading ? (
                        <>
                          <BiLoaderAlt className="animate-spin -ml-1 mr-3 h-5 w-5" />
                          LOGGING IN...
                        </>
                      ) : (
                        <>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-6 w-6 mr-2"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z"
                              clipRule="evenodd"
                            />
                          </svg>
                          LOGIN
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
